import type { PartialContent, PartialMoment } from '../momentTypes'
import { WeekViewSortType } from '../types'
import { useWeekViewSettings } from '../WeekViewSettingsContext'

type LabelWithColor = {
  id?: string
  name?: string
  color?: string
  label_set?: {
    id?: string
    name?: string
  }
}

/**
 * Resolves the stripe color hex for a content card from the "Color By" view
 * setting. The default "Sort" option follows whatever attribute drives the
 * "Sort By" grouping; an explicit choice overrides it. The effective attribute
 * maps to a color the same way:
 *
 *  - Channel / Account → the content's channel/service color (an account
 *    inherits its channel's color).
 *  - a label set → the first label from that set carried by the content
 *    (falling back to the parent moment).
 *  - Time / No Grouping → these carry no color of their own, so no stripe is
 *    rendered.
 *
 * Returns a `#rrggbb` string, or `null` when the active attribute supplies no
 * color. Callers should render no stripe when `null` is returned.
 */
export const useCardStripeColor = (
  content: PartialContent,
  moment: PartialMoment | undefined,
  channelColor?: string
): string | null => {
  const { colorBy, selectedSort } = useWeekViewSettings()

  // The default "Color By: Sort" (the None-typed sentinel) defers to the sort
  // attribute; any other choice is the color source directly.
  const source = colorBy.type === WeekViewSortType.None ? selectedSort : colorBy

  if (
    source.type === WeekViewSortType.Channel ||
    source.type === WeekViewSortType.Account
  ) {
    return channelColor ?? null
  }

  if (source.type !== WeekViewSortType.LabelSet) return null

  const matchedColor =
    findLabelColor(content.labels, source.id) ??
    findLabelColor(moment?.labels, source.id)

  if (!matchedColor) return null
  return matchedColor.startsWith('#') ? matchedColor : `#${matchedColor}`
}

const findLabelColor = (
  labels: LabelWithColor[] | undefined,
  setId: string
): string | null => {
  if (!labels) return null
  for (const label of labels) {
    if (String(label.label_set?.id) === String(setId) && label.color) {
      return label.color
    }
  }
  return null
}
